import { isEqual } from 'lodash';
import React from 'react';
import { PiTrashDuotone } from 'react-icons/pi';
import { Button } from 'rizzui';
import CustomSearchableEntities from '../../../components/forms/CustomSearchableEntities';
import CustomSelect from '../../../components/forms/CustomSelect';
import DateField from '../../../components/forms/DateField';
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useCorporations from '../../../hooks/api-calls/useCorporations';
import useProvidingCorporations from '../../../hooks/api-calls/useProvidingCorporations';
import useTutors from '../../../hooks/api-calls/useTutors';
import { FilterOptions } from '../../../hooks/useFilters';
import { INVOICE_TYPES } from './InvoicesList';

interface InvoicesFiltersProps {
    updateFilters: (filters: any) => void
    resetFilters: (limit: any) => void
    filters: FilterOptions
}

const InvoicesFilters: React.FC<InvoicesFiltersProps> = ({ filters, updateFilters, resetFilters }) => {

    const { userCan } = usePrivilege();
    const { fetchCorporations } = useCorporations();
    const { fetchProvidingCorporations } = useProvidingCorporations();
    const { getTutorsList, updateTutorsHookFilters } = useTutors();

    const allowedInvoiceTypes = INVOICE_TYPES.filter(type => {
        if (!type.permission) return true; // Si no hay permiso, se permite
        return userCan(type.permission, 'invoices'); // Comprobar si el usuario tiene el permiso
    });

    return (
        <>
            {allowedInvoiceTypes.length > 1 && (
                <CustomSelect
                    id="invoice_type"
                    isMulti
                    label="Tipo de Factura"
                    className="w-full"
                    options={allowedInvoiceTypes}
                    onChange={(e: any) => {
                        const selectedValues = e.map((item: any) => item.value);
                        updateFilters({ invoice_type: selectedValues.length > 0 ? selectedValues : null });
                    }}
                    value={
                        filters.filter_filters?.invoice_type
                            ? INVOICE_TYPES.filter((item: any) => filters.filter_filters?.invoice_type.includes(item.value))
                            : null
                    }
                />
            )}

            <CustomSearchableEntities
                id="corporations"
                label="Empresa Fiscal"
                filters={filters}
                updateFilters={updateFilters}
                fetchOptions={async (searchText: string | null) => {
                    return fetchCorporations({ search_text: searchText, ids: filters.filter_filters?.client ?? [] });
                }}
                permissions={[
                    {
                        group: 'corporation',
                        action: 'get_corporations_for_select'
                    }
                ]}
            />

            <CustomSearchableEntities
                id="providing_corporations"
                label="Organización Impartidora"
                filters={filters}
                updateFilters={updateFilters}
                fetchOptions={async (searchText: string | null) => {
                    return fetchProvidingCorporations({ search_text: searchText, ids: filters.filter_filters?.client ?? [] });
                }}
                permissions={[
                    {
                        group: 'corporation',
                        action: 'get_corporations_for_select'
                    }
                ]}
            />

            <CustomSearchableEntities
                id="tutor"
                label="Tutor"
                filters={filters}
                updateFilters={updateFilters}
                fetchOptions={async (searchText: string | null) => {
                    updateTutorsHookFilters({ search_text: searchText });
                    return getTutorsList();
                }}
                permissions={[
                    {
                        group: 'tutors',
                        action: 'get_tutors_for_select'
                    }
                ]}
            />

            <DateField
                isClearable
                className="w-full"
                label="Fecha de Creación"
                placeholderText="Selecciona las fechas"
                selectedDateField={filters.filter_filters?.between_dates?.type || "createdAt"}
                dateTypesOptions={[
                    { label: "Fecha de creación", value: "createdAt" },
                    { label: "Fecha de modificación", value: "updatedAt" },
                    { label: "Fecha de factura", value: "invoiceDate" },
                    { label: "Fecha de pago", value: "paymentDate" },
                    { label: "Fecha de vencimiento", value: "expirationDate" },
                ]}
                startDate={filters.filter_filters?.between_dates?.startDate ? new Date(filters.filter_filters.between_dates.startDate) : null}
                endDate={filters.filter_filters?.between_dates?.endDate ? new Date(filters.filter_filters.between_dates.endDate) : null}
                onChange={(date: any) => {
                    const filters_between_dates = filters.filter_filters?.between_dates;
                    const between_dates = date ? { startDate: date[0], endDate: date[1] } : null;

                    if (between_dates && !isEqual(filters_between_dates, between_dates)) {
                        updateFilters({ between_dates: { startDate: date[0], endDate: date[1], type: date[2] } });
                    } else if (!between_dates && filters_between_dates) {
                        updateFilters({ between_dates: null });
                    }
                }}

            />

            <Button
                size="sm"
                onClick={() => {
                    resetFilters(50);
                }}
                variant="flat"
                className="h-9 bg-gray-200/70"
            >
                <PiTrashDuotone className="me-1.5 h-[17px] w-[17px]" /> Limpiar
            </Button>
        </>
    );
};

export default InvoicesFilters;